/**
 * Database Export Component
 */

import { useState, useEffect } from 'react';
import apiFetch, { getToken, getApiBaseUrl } from '../../services/api';
import Toast from '../../components/Toast';
import './Settings.css';

interface TableInfo {
  name: string;
  row_count: number;
  columns: string[];
}

interface TablesResponse {
  tables: TableInfo[];
  total_tables: number;
}

const DatabaseExport = () => {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState<'sql' | 'json' | 'csv'>('sql');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: TablesResponse = await apiFetch('/api/v1/database-export/tables');
      setTables(response.tables);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden van tabellen');
    } finally {
      setLoading(false);
    }
  };

  const handleTableToggle = (tableName: string) => {
    setSelectedTables(prev =>
      prev.includes(tableName)
        ? prev.filter(t => t !== tableName)
        : [...prev, tableName]
    );
  };

  const handleSelectAll = () => {
    if (selectedTables.length === tables.length) {
      setSelectedTables([]);
    } else {
      setSelectedTables(tables.map(t => t.name));
    }
  };

  const handleExport = async () => {
    if (selectedTables.length === 0) {
      setError('Selecteer minimaal één tabel om te exporteren');
      return;
    }

    try {
      setExporting(true);
      setError(null);
      setSuccess(null);

      const token = getToken();
      const baseUrl = getApiBaseUrl();

      const response = await fetch(`${baseUrl}/api/v1/database-export/export?format=${exportFormat}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(selectedTables)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Export mislukt');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      // Default filename with correct extension
      let fileExtension: string = exportFormat;
      if (exportFormat === 'csv' && selectedTables.length > 1) {
        fileExtension = 'zip';
      }
      let filename = `buro42_export.${fileExtension}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(`Export succesvol! ${selectedTables.length} tabel(len) geëxporteerd als ${exportFormat.toUpperCase()}`);
    } catch (err: any) {
      setError(err.message || 'Fout bij exporteren');
    } finally {
      setExporting(false);
    }
  };

  const getTotalRows = () => {
    return selectedTables.reduce((total, tableName) => {
      const table = tables.find(t => t.name === tableName);
      return total + (table?.row_count || 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Database Export</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          Tabellen laden...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Database Export</h1>
      </div>

      {error && (
        <div className="error-message" style={{
          padding: '1rem',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      <div style={{
        backgroundColor: '#f8fafc',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginBottom: '1rem', color: '#1e40af' }}>Export Instellingen</h3>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
            Export Formaat:
          </label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                name="format"
                value="sql"
                checked={exportFormat === 'sql'}
                onChange={() => setExportFormat('sql')}
              />
              SQL (INSERT statements)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                name="format"
                value="json"
                checked={exportFormat === 'json'}
                onChange={() => setExportFormat('json')}
              />
              JSON
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="radio"
                name="format"
                value="csv"
                checked={exportFormat === 'csv'}
                onChange={() => setExportFormat('csv')}
              />
              CSV {selectedTables.length > 1 ? '(ZIP bestand)' : ''}
            </label>
          </div>
        </div>

        <div style={{
          padding: '1rem',
          backgroundColor: '#fff',
          borderRadius: '4px',
          border: '1px solid #e2e8f0'
        }}>
          <strong>Geselecteerd:</strong> {selectedTables.length} van {tables.length} tabellen
          <br />
          <strong>Totaal aantal rijen:</strong> {getTotalRows().toLocaleString()}
        </div>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem'
        }}>
          <h3 style={{ margin: 0 }}>Selecteer Tabellen</h3>
          <button
            onClick={handleSelectAll}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#e2e8f0',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {selectedTables.length === tables.length ? 'Deselecteer Alles' : 'Selecteer Alles'}
          </button>
        </div>

        <div style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #e2e8f0',
          borderRadius: '8px'
        }}>
          {tables.map(table => (
            <div
              key={table.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem 1rem',
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: selectedTables.includes(table.name) ? '#eff6ff' : '#fff',
                cursor: 'pointer'
              }}
              onClick={() => handleTableToggle(table.name)}
            >
              <input
                type="checkbox"
                checked={selectedTables.includes(table.name)}
                onChange={() => handleTableToggle(table.name)}
                style={{ marginRight: '1rem' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {table.name}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {table.row_count.toLocaleString()} rijen | {table.columns.length} kolommen
                </div>
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: '#94a3b8',
                maxWidth: '300px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {table.columns.slice(0, 5).join(', ')}
                {table.columns.length > 5 && '...'}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <button
          onClick={handleExport}
          disabled={exporting || selectedTables.length === 0}
          style={{
            padding: '0.75rem 2rem',
            backgroundColor: selectedTables.length === 0 ? '#94a3b8' : '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 'bold',
            cursor: selectedTables.length === 0 ? 'not-allowed' : 'pointer',
            opacity: exporting ? 0.7 : 1
          }}
        >
          {exporting ? 'Exporteren...' : `Exporteer ${selectedTables.length} Tabel(len)`}
        </button>

        <button
          onClick={loadTables}
          disabled={loading}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#f1f5f9',
            color: '#475569',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Vernieuwen
        </button>
      </div>

      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        backgroundColor: '#fef3c7',
        borderRadius: '8px',
        fontSize: '0.9rem',
        color: '#92400e'
      }}>
        <strong>Let op:</strong> Database exports bevatten alle gegevens inclusief wachtwoord-hashes.
        Bewaar exports veilig en deel ze niet onbeveiligd.
      </div>
    </div>
  );
};

export default DatabaseExport;
